import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { format, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import { Calendar, ChevronLeft, ChevronRight, BarChart3, Clock, GitCompare, Zap, AlertTriangle } from 'lucide-react';
import { DayProps } from 'react-day-picker';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';

import { Card, CardContent } from '@/components/ui/card';
import { DayChart } from './DayChart';
import type { QuarterData, TimeGranularity, AnalysisConfig } from '@/types/piekanalyse';
import { calculateDailyKWh, calculateThreshold, getDayStatus } from '@/lib/piekvermoigenParser';

interface DayViewerDialogProps {
  open: boolean;
  onClose: () => void;
  rawData: Record<string, { quarters: QuarterData[] }> | undefined;
  config: AnalysisConfig;
  targetSolutionKWh?: number;
  initialDate?: string;
  onCompare?: (date: string) => void;
}

export const DayViewerDialog: React.FC<DayViewerDialogProps> = ({
  open,
  onClose,
  rawData,
  config,
  targetSolutionKWh = 0,
  initialDate,
  onCompare,
}) => {
  const availableDates = useMemo(() => {
    if (!rawData) return [];
    return Object.keys(rawData).sort();
  }, [rawData]);

  const [selectedDate, setSelectedDate] = useState<string>(
    initialDate || availableDates[0] || ''
  );
  const [granularity, setGranularity] = useState<TimeGranularity>('quarter');

  // Set initial date when provided
  useEffect(() => {
    if (initialDate && availableDates.includes(initialDate)) {
      setSelectedDate(initialDate);
    }
  }, [initialDate, availableDates]);

  // Reset selected date when dialog opens with new data
  useEffect(() => {
    if (open && availableDates.length > 0 && !availableDates.includes(selectedDate)) {
      setSelectedDate(availableDates[0]);
    }
  }, [open, availableDates, selectedDate]);

  const availableDateSet = useMemo(() => new Set(availableDates), [availableDates]);

  // First available date for calendar defaultMonth
  const firstAvailableDate = useMemo(() => {
    if (availableDates.length === 0) return undefined;
    return parseISO(availableDates[0]);
  }, [availableDates]);

  // Calculate consumption range for color scale
  const consumptionRange = useMemo(() => {
    if (!rawData) return { min: 0, max: 1 };
    
    const values = Object.values(rawData).map(day => 
      calculateDailyKWh(day.quarters)
    );
    
    return {
      min: Math.min(...values),
      max: Math.max(...values),
    };
  }, [rawData]);

  // Get consumption color based on kWh (#014336 with variable opacity)
  const getConsumptionColor = (kWh: number): string => {
    const { min, max } = consumptionRange;
    const range = max - min || 1;
    const normalized = (kWh - min) / range;
    const opacity = 0.15 + (normalized * 0.55);
    return `rgba(1, 67, 54, ${opacity.toFixed(2)})`;
  };

  const selectedDayData = useMemo(() => {
    if (!rawData || !selectedDate) return null;
    return rawData[selectedDate];
  }, [rawData, selectedDate]);

  // Calculate consumption stats for selected day
  const consumptionStats = useMemo(() => {
    if (!selectedDayData) return null;
    
    const totalKWh = calculateDailyKWh(selectedDayData.quarters);
    const threshold = calculateThreshold(config.contractedCapacity, targetSolutionKWh);
    const status = getDayStatus(totalKWh, config.contractedCapacity, targetSolutionKWh);
    const difference = totalKWh - threshold;
    
    return { totalKWh, threshold, status, difference };
  }, [selectedDayData, config.contractedCapacity, targetSolutionKWh]);

  const navigateDay = (direction: 'prev' | 'next') => {
    const currentIndex = availableDates.indexOf(selectedDate);
    if (direction === 'prev' && currentIndex > 0) {
      setSelectedDate(availableDates[currentIndex - 1]);
    } else if (direction === 'next' && currentIndex < availableDates.length - 1) {
      setSelectedDate(availableDates[currentIndex + 1]);
    }
  };

  const currentIndex = availableDates.indexOf(selectedDate);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < availableDates.length - 1;

  // Custom Day component with consumption-based coloring
  const DayWithConsumption = useCallback((props: DayProps) => {
    const { date, displayMonth, ...rest } = props;
    const dateStr = format(date, 'yyyy-MM-dd');
    const isAvailable = availableDateSet.has(dateStr);
    const isSelected = selectedDate === dateStr;
    const isOutsideMonth = displayMonth && date.getMonth() !== displayMonth.getMonth();
    
    // Calculate background color for available days
    let backgroundColor = 'transparent';
    if (isAvailable && rawData && rawData[dateStr]) {
      const kWh = calculateDailyKWh(rawData[dateStr].quarters);
      backgroundColor = getConsumptionColor(kWh);
    }
    
    return (
      <button
        {...rest}
        disabled={!isAvailable}
        onClick={() => {
          if (isAvailable) {
            setSelectedDate(dateStr);
          }
        }}
        style={{ backgroundColor: isSelected ? undefined : backgroundColor }}
        className={cn(
          buttonVariants({ variant: "ghost" }),
          "h-9 w-9 p-0 font-normal rounded-full transition-colors",
          isSelected && "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
          !isAvailable && "text-muted-foreground opacity-50 cursor-not-allowed",
          isOutsideMonth && "text-muted-foreground opacity-50"
        )}
      >
        {date.getDate()}
      </button>
    );
  }, [availableDateSet, rawData, selectedDate, getConsumptionColor]);

  const formatKWh = (value: number) => {
    if (Math.abs(value) >= 1000) {
      return `${(value / 1000).toFixed(1)} MWh`;
    }
    return `${Math.round(value)} kWh`;
  };

  if (!rawData) {
    return (
      <Dialog open={open} onOpenChange={() => onClose()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Dag Verkenner
            </DialogTitle>
          </DialogHeader>
          <div className="text-center py-12 text-muted-foreground">
            <BarChart3 className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p className="font-medium">Geen dagdata beschikbaar</p>
            <p className="text-sm mt-1">
              Deze analyse is gemaakt voor de uitgebreide dag-weergave beschikbaar was.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-7xl h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Dag Verkenner
            <Badge variant="outline" className="ml-2">
              {availableDates.length} dagen beschikbaar
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6 min-h-0">
          {/* Left: Calendar - fixed */}
          <div className="space-y-4">
            <CalendarComponent
              mode="single"
              selected={selectedDate ? parseISO(selectedDate) : undefined}
              defaultMonth={firstAvailableDate}
              onSelect={(date) => {
                if (date) {
                  const dateStr = format(date, 'yyyy-MM-dd');
                  if (availableDateSet.has(dateStr)) {
                    setSelectedDate(dateStr);
                  }
                }
              }}
              disabled={(date) => {
                const dateStr = format(date, 'yyyy-MM-dd');
                return !availableDateSet.has(dateStr);
              }}
              components={{
                Day: DayWithConsumption,
              }}
              className="rounded-md border pointer-events-auto"
            />

            <div className="space-y-1.5 pt-3 border-t">
              <p className="text-xs font-medium text-muted-foreground">Dagverbruik</p>
              <div 
                className="h-2.5 rounded-full"
                style={{
                  background: 'linear-gradient(to right, rgba(1,67,54,0.15), rgba(1,67,54,0.70))'
                }}
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>{formatKWh(consumptionRange.min)}</span>
                <span>{formatKWh(consumptionRange.max)}</span>
              </div>
            </div>
          </div>

          {/* Right: Scrollable content */}
          <div className="overflow-y-auto pr-2 space-y-4 min-h-0">
            {/* Date Header */}
            <div className="bg-muted/50 rounded-lg p-4">
              <h3 className="text-lg font-semibold">
                {selectedDate && format(parseISO(selectedDate), 'EEEE d MMMM yyyy', { locale: nl })}
              </h3>
            </div>

            {/* Consumption Stats Cards */}
            {consumptionStats && (
              <div className="grid grid-cols-2 gap-4">
                <Card className={consumptionStats.status === 'red' ? 'border-destructive' : 'border-emerald-500'}>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <Zap className="h-4 w-4" />
                      <span className="text-sm">Totaal Verbruik</span>
                    </div>
                    <p className="text-2xl font-bold">{formatKWh(consumptionStats.totalKWh)}</p>
                    <p className={`text-sm mt-1 ${consumptionStats.status === 'red' ? 'text-destructive' : 'text-emerald-600'}`}>
                      {consumptionStats.status === 'red' ? (
                        <span className="flex items-center gap-1">
                          <AlertTriangle className="h-3 w-3" />
                          +{formatKWh(consumptionStats.difference)} boven threshold
                        </span>
                      ) : (
                        <span>✓ {formatKWh(Math.abs(consumptionStats.difference))} onder threshold</span>
                      )}
                    </p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 text-muted-foreground mb-1">
                      <BarChart3 className="h-4 w-4" />
                      <span className="text-sm">Beoogde Oplossing</span>
                    </div>
                    <p className="text-2xl font-bold">{formatKWh(consumptionStats.threshold)}</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      (24u × {config.contractedCapacity} + {targetSolutionKWh.toLocaleString()})
                    </p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Controls */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => navigateDay('prev')}
                  disabled={!hasPrev}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => navigateDay('next')}
                  disabled={!hasNext}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>

              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-muted-foreground" />
                  <Select value={granularity} onValueChange={(v) => setGranularity(v as TimeGranularity)}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="quarter">Kwartier</SelectItem>
                      <SelectItem value="hour">Uur</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {onCompare && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onCompare(selectedDate)}
                  >
                    <GitCompare className="h-4 w-4 mr-2" />
                    Vergelijk
                  </Button>
                )}
              </div>
            </div>

            {/* Chart */}
            {selectedDayData ? (
              <DayChart
                quarters={selectedDayData.quarters}
                granularity={granularity}
                contractedCapacity={config.contractedCapacity}
                showReturnDelivery={config.includeReturnDelivery}
                height={450}
              />
            ) : (
              <div className="h-[450px] flex items-center justify-center bg-muted/30 rounded-lg">
                <p className="text-muted-foreground">Selecteer een dag in de kalender</p>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DayViewerDialog;
